/**
 * React Hooks for WebSocket Connections
 *
 * Provides real-time updates for tasks and live data.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './useApi';
import WebSocketService, { createTaskSocket, createLiveDataSocket } from '../services/websocket';

// =============================================================================
// Task WebSocket Hook
// =============================================================================

interface TaskProgress {
  task_id: string;
  status: string;
  progress: number;
  message?: string;
  iteration?: number;
  residual?: number;
}

interface TaskResult {
  task_id: string;
  status: 'completed' | 'failed';
  run_id?: string;
  error?: string;
}

interface UseTaskSocketOptions {
  onProgress?: (progress: TaskProgress) => void;
  onComplete?: (result: TaskResult) => void;
  onError?: (error: Error) => void;
}

export function useTaskSocket(taskId: string | null, options: UseTaskSocketOptions = {}) {
  const { onProgress, onComplete, onError } = options;
  const socketRef = useRef<WebSocketService | null>(null);
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const [lastProgress, setLastProgress] = useState<TaskProgress | null>(null);

  useEffect(() => {
    if (!taskId) {
      return;
    }

    const socket = createTaskSocket(taskId);
    socketRef.current = socket;

    // Connect and set up handlers
    socket.connect()
      .then(() => {
        setIsConnected(true);
      })
      .catch((error) => {
        console.error('Task socket connection failed:', error);
        onError?.(error);
      });

    const handleProgress = (payload: any) => {
      const progressPayload: TaskProgress = {
        task_id: payload?.task_id || taskId || '',
        status: payload?.status || 'running',
        progress: typeof payload?.progress === 'number' ? payload.progress : 0,
        message: payload?.message,
        iteration: payload?.iteration,
        residual: payload?.residual ?? payload?.max_p_mismatch ?? payload?.max_mismatch,
      };

      setLastProgress(progressPayload);
      onProgress?.(progressPayload);
      queryClient.invalidateQueries({ queryKey: queryKeys.task(taskId) });
    };

    const handleComplete = (payload: any, status: 'completed' | 'failed') => {
      const resultPayload: TaskResult = {
        task_id: payload?.task_id || taskId || '',
        status,
        run_id: payload?.run_id,
        error: payload?.error,
      };

      onComplete?.(resultPayload);

      // Invalidate queries to reflect completion
      queryClient.invalidateQueries({ queryKey: queryKeys.task(taskId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      queryClient.invalidateQueries({ queryKey: queryKeys.runs });

      // Disconnect after completion
      socket.disconnect();
      setIsConnected(false);
    };

    // Progress updates (support both legacy and server message types)
    socket.subscribe('progress', handleProgress);
    socket.subscribe('task_progress', handleProgress);
    socket.subscribe('task_iteration', handleProgress);

    // Task completed (support both legacy and server message types)
    socket.subscribe('completed', (payload: TaskResult) => handleComplete(payload, 'completed'));
    socket.subscribe('task_completed', (payload: any) => handleComplete(payload, 'completed'));

    // Task failed (support both legacy and server message types)
    socket.subscribe('failed', (payload: TaskResult) => handleComplete(payload, 'failed'));
    socket.subscribe('task_failed', (payload: any) => handleComplete(payload, 'failed'));

    // Error handling
    socket.subscribe('error', (payload: { message: string }) => {
      onError?.(new Error(payload.message));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [taskId, onProgress, onComplete, onError, queryClient]);

  const cancel = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.send({ type: 'cancel' });
    }
  }, []);

  return {
    isConnected,
    lastProgress,
    cancel,
  };
}

// =============================================================================
// Live Data WebSocket Hook
// =============================================================================

interface LiveDataMessage {
  type: 'price_update' | 'generation_update' | 'hvdc_update' | 'outage_update';
  timestamp: string;
  data: any;
}

interface UseLiveDataSocketOptions {
  onPriceUpdate?: (data: any) => void;
  onGenerationUpdate?: (data: any) => void;
  onHvdcUpdate?: (data: any) => void;
  onOutageUpdate?: (data: any) => void;
  autoSubscribe?: string[]; // e.g., ['prices', 'generation']
  enabled?: boolean;
}

export function useLiveDataSocket(options: UseLiveDataSocketOptions = {}) {
  const {
    onPriceUpdate,
    onGenerationUpdate,
    onHvdcUpdate,
    onOutageUpdate,
    autoSubscribe = [],
    enabled = true,
  } = options;
  const socketRef = useRef<WebSocketService | null>(null);
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsConnected(false);
      return;
    }
    const socket = createLiveDataSocket();
    socketRef.current = socket;

    socket.connect()
      .then(() => {
        setIsConnected(true);

        // Auto-subscribe to requested channels
        if (autoSubscribe.length > 0) {
          socket.send({
            type: 'subscribe',
            channels: autoSubscribe,
          });
        }
      })
      .catch((error) => {
        console.error('Live data socket connection failed:', error);
      });

    // Price updates
    socket.subscribe('price_update', (data) => {
      onPriceUpdate?.(data);
      queryClient.invalidateQueries({ queryKey: queryKeys.witsPrices });
    });

    // Generation updates
    socket.subscribe('generation_update', (data) => {
      onGenerationUpdate?.(data);
      queryClient.invalidateQueries({ queryKey: queryKeys.witsGeneration });
    });

    // HVDC updates
    socket.subscribe('hvdc_update', (data) => {
      onHvdcUpdate?.(data);
      queryClient.invalidateQueries({ queryKey: queryKeys.transpowerHvdc });
    });

    // Outage updates
    socket.subscribe('outage_update', (data) => {
      onOutageUpdate?.(data);
      queryClient.invalidateQueries({ queryKey: queryKeys.transpowerOutages });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [
    autoSubscribe,
    onPriceUpdate,
    onGenerationUpdate,
    onHvdcUpdate,
    onOutageUpdate,
    queryClient,
    enabled,
  ]);

  const subscribe = useCallback((channels: string[]) => {
    if (socketRef.current) {
      socketRef.current.send({
        type: 'subscribe',
        channels,
      });
    }
  }, []);

  const unsubscribe = useCallback((channels: string[]) => {
    if (socketRef.current) {
      socketRef.current.send({
        type: 'unsubscribe',
        channels,
      });
    }
  }, []);

  return {
    isConnected,
    subscribe,
    unsubscribe,
  };
}

// =============================================================================
// Solver Progress Hook (Convenience wrapper)
// =============================================================================

export function useSolverProgress(taskId: string | null) {
  const [progress, setProgress] = useState(0);
  const [iteration, setIteration] = useState(0);
  const [residual, setResidual] = useState<number | null>(null);
  const [status, setStatus] = useState<string>('pending');
  const [message, setMessage] = useState<string>('');
  const [result, setResult] = useState<TaskResult | null>(null);

  const { isConnected, cancel } = useTaskSocket(taskId, {
    onProgress: (data) => {
      setProgress(data.progress);
      setStatus(data.status);
      setMessage(data.message || '');
      if (data.iteration !== undefined) setIteration(data.iteration);
      if (data.residual !== undefined) setResidual(data.residual);
    },
    onComplete: (data) => {
      setResult(data);
      setStatus(data.status);
      setProgress(100);
    },
  });

  return {
    isConnected,
    progress,
    iteration,
    residual,
    status,
    message,
    result,
    cancel,
    isComplete: result !== null,
    isSuccess: result?.status === 'completed',
    isFailed: result?.status === 'failed',
  };
}
